import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { compareArraysEqual } from '../utils';
import { batchCompareTime } from '../utils/time';
import { FILTER_CONST } from '../constants';
import {
  KeywordSearchModelType,
  KeywordListModeType,
  PeriodDateType,
  ServerTimeType,
} from '../types';
import { useStateRef, useSyncState } from '@op/hooks/common';
import { isNilOrEmpty } from '@op/utils/value';
import { DBO_D, DBO_TM } from '@op/utils/format';
import { isValidExpression, parseRule } from '../widgets/KeyWordsSelect/utils';
import { useUpdateEffect } from 'ahooks';

/**
 * 设置选项的默认值的hook
 * @param defaultValue   默认的对象
 * @param filterValue   实时同步的对象
 * @param defaultKey   默认的key
 * @param selects      选中项
 * @param setSelects   设置选中项
 * @param options      全选项
 * @param watchOptions 监听全选项，因为有些数据是通过接口获取，导致数据没有那么快
 */
export function useSetSelectDefaultValue<T>({
  defaultValue,
  filterValue,
  oriDefaultFilter = {},
  backOriDefaultValue,
  defaultKey,
  options,
  mustFilter,
  noneIsAll = false,
  mustFilterValueFunc = (value: string) => value,
  firstExcludeOptions,
  watchOptions,
  finallyDealwithSelected = selected => selected,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  oriDefaultFilter?: { [key: string]: any };
  defaultKey: string;
  options: Array<T>;
  mustFilter?: boolean;
  noneIsAll?: boolean;
  mustFilterValueFunc?: (data: any) => any;
  firstExcludeOptions?: Array<T>;
  watchOptions?: boolean;
  finallyDealwithSelected?: (
    selected: Array<T>,
    options?: Array<T>,
  ) => Array<T>;
  backOriDefaultValue?: (value: any) => void;
}): {
  selects: Array<T>;
  setSelects: Dispatch<SetStateAction<Array<T>>>;
  isFirst: Boolean;
  diffClassName: string | undefined;
} {
  const [selects, setSelects] = useState<Array<T>>([]);
  const oriDefaultValueRef = useRef<Array<T>>([]);
  const isFirst = useRef(true);

  const defaultValueSet = (
    data: any,
    currentSelect: Array<T>,
    firstExcludeOptions?: Array<T>,
    isFirstValue = false,
  ) => {
    let defaultInfo = Array.isArray(data[defaultKey]) ? data[defaultKey] : [];

    if (
      compareArraysEqual<T>(defaultInfo, currentSelect) &&
      currentSelect.length !== 0
    ) {
      return;
    }
    let resultSelect = [];

    if (
      defaultInfo?.length === 1 &&
      ['-99999', -99999].includes(defaultInfo[0])
    ) {
      // 全不选的情况，直接不对数组进行操作
    } else if (
      defaultInfo?.length === 1 &&
      ['99999', 99999].includes(defaultInfo[0])
    ) {
      resultSelect = defaultInfo;
    }
    // 如果没有默认值，或者默认值没有长度，那么就直接设置所有数据
    else if (!defaultInfo || defaultInfo?.length === 0) {
      let newOptions = options;
      if (firstExcludeOptions) {
        newOptions = newOptions.filter(
          item => !firstExcludeOptions.includes(item),
        );
      }
      resultSelect = newOptions;
    } else {
      if (mustFilter) {
        // 过滤总数据都不存在的一部分数据
        defaultInfo = defaultInfo.filter((item: T) =>
          options.includes(mustFilterValueFunc(item)),
        );
      }
      // 如果过滤后成为了空数组了，然后说空数组为全选，就设置全选
      if (defaultInfo.length === 0 && noneIsAll) {
        resultSelect = options;
      } else {
        resultSelect = defaultInfo;
      }
    }
    const result = finallyDealwithSelected(resultSelect, options);
    return result;
  };

  useEffect(() => {
    const localDefaultValue = defaultValueSet(
      defaultValue,
      selects,
      firstExcludeOptions,
      isFirst.current,
    );
    if (localDefaultValue) {
      setSelects(localDefaultValue);
      isFirst.current = false;
    }

    if (!watchOptions) {
      const localOriDefaultValue = defaultValueSet(
        oriDefaultFilter,
        oriDefaultValueRef.current,
        firstExcludeOptions,
        true,
      );

      if (localOriDefaultValue) {
        oriDefaultValueRef.current = localOriDefaultValue;
        backOriDefaultValue?.({ [defaultKey]: localOriDefaultValue });
      }
    }
  }, []);

  useEffect(() => {
    if (!isFirst.current && filterValue[defaultKey]) {
      const value = defaultValueSet(filterValue, selects, firstExcludeOptions);
      value && setSelects(value);
    }
  }, [filterValue]);

  const oldOptions = useRef(options);
  useEffect(() => {
    if (
      watchOptions &&
      options.length > 0 &&
      !compareArraysEqual<T>(options, selects) &&
      !compareArraysEqual<T>(oldOptions.current, options)
    ) {
      const value = defaultValueSet(defaultValue, selects);
      value && setSelects(value);
      oldOptions.current = options;

      if (oriDefaultValueRef.current.length === 0) {
        const localOriDefaultValue = defaultValueSet(
          oriDefaultFilter,
          oriDefaultValueRef.current,
          firstExcludeOptions,
          true,
        );
        if (localOriDefaultValue) {
          oriDefaultValueRef.current = localOriDefaultValue;
          backOriDefaultValue?.({ [defaultKey]: localOriDefaultValue });
        }
      }
    }
  }, [options]);

  const diffClassName = useMemo(() => {
    if (!DBO_D.compareArray(selects, oriDefaultValueRef.current, true)) {
      return 'dbo-diff-filter';
    }
  }, [selects]);

  return { selects, setSelects, isFirst: isFirst.current, diffClassName };
}

/**
 * 设置时间默认值的hook
 * @param defaultValue 默认的对象
 * @param defaultKey   默认的key
 * @param selects      选中项
 * @param setSelects   设置选中项
 * @param options      全选项
 */
export function useSetTimeDefaultValue({
  defaultValue,
  filterValue,
  oriDefaultFilter = {},
}: // backOriDefaultValue,
{
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  oriDefaultFilter?: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
}): {
  timeValue: Array<string>;
  setTimeValue: Dispatch<SetStateAction<Array<string>>>;
  dateType: string;
  setDateType: Dispatch<SetStateAction<string>>;
  diffClassName?: string;
} {
  const [timeValue, setTimeValue] = useState<Array<string>>([]);
  const [dateType, setDateType] = useState('daily');
  const oriDefaultValueRef = useRef<any>({
    dateType: 'daily',
    timeValue: [],
  });
  const isFirst = useRef(true);
  const dealwithDate = (data: any = {}) => {
    let { start_time, end_time } = data;
    const { date_type } = data;

    const localDateType = date_type || 'daily';

    if (!start_time) {
      const { start_time: startTime, end_time: endTime } =
        DBO_TM.today(localDateType);
      start_time = startTime;
      end_time = endTime;
    }

    return {
      dateType: localDateType,
      timeValue: [start_time, end_time],
    };
  };

  useEffect(() => {
    const { dateType: localDateType, timeValue: localTimeValue } =
      dealwithDate(defaultValue);

    if (localDateType !== dateType) {
      setDateType(localDateType);
    }
    if (!batchCompareTime(timeValue, localTimeValue)) {
      setTimeValue(localTimeValue);
    }

    oriDefaultValueRef.current = dealwithDate(oriDefaultFilter);

    isFirst.current = false;
  }, []);

  useEffect(() => {
    if (!isFirst.current && filterValue?.start_time) {
      const { dateType: localDateType, timeValue: localTimeValue } =
        dealwithDate(filterValue);

      if (localDateType !== dateType) {
        setDateType(localDateType);
      }
      if (!batchCompareTime(timeValue, localTimeValue)) {
        setTimeValue(localTimeValue);
      }
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    const { dateType: localDateType, timeValue: localTimeValue } =
      oriDefaultValueRef.current;

    if (
      localDateType !== dateType ||
      !batchCompareTime(timeValue, localTimeValue)
    ) {
      return 'dbo-diff-filter';
    }
  }, [dateType, timeValue]);

  return { dateType, setDateType, timeValue, setTimeValue, diffClassName };
}

export function useGetCustomSelectButtonElement() {
  const ref = useRef<any>(null);

  const getButtonElement = useCallback(() => ref.current?.buttonElement, []);

  return {
    customSelectRef: ref,
    getButtonElement,
  };
}

export function useSetStringDefaultValue({
  defaultValue,
  filterValue,
  defaultKey,
  initValue,
  dealwithValue = (value: number | string) => `${value}`,
}: // oriDefaultFilter,
// backOriDefaultValue,
{
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  defaultKey: string;
  initValue: string;
  dealwithValue?: (value: string | number) => string;
  oriDefaultFilter?: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
}): {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  isFirst: boolean;
  diffClassName?: string;
} {
  const [value, setValue] = useState<string>(initValue);
  const oriDefaultValueRef = useRef<string>(initValue);
  const isFirst = useRef(true);

  useEffect(() => {
    isFirst.current = false;
    const value = dealwithValue(defaultValue[defaultKey]) || initValue;
    setValue(value);

    // oriDefaultValueRef.current = value;

    // console.log(oriDefaultValueRef.current);
  }, []);

  useEffect(() => {
    if (!isFirst.current && !isNilOrEmpty(filterValue[defaultKey])) {
      setValue(dealwithValue(filterValue[defaultKey]));
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    if (String(value) !== String(oriDefaultValueRef.current)) {
      return 'dbo-diff-filter';
    }
  }, [value]);

  return { value, setValue, isFirst: isFirst.current, diffClassName };
}

export function useSetEngagementDefaultValue({
  defaultValue,
  filterValue,
  defaultKey,
  options,
  oriDefaultFilter,
}: // backOriDefaultValue,
{
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  defaultKey: Array<string>;
  options: Array<string>;
  oriDefaultFilter?: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
}): [
  Array<string>,
  Dispatch<SetStateAction<Array<string>>>,
  boolean,
  string | undefined,
] {
  const [value, setValue] = useState<Array<string>>([]);
  const oriDefaultValueRef = useRef<Array<string>>([]);
  const isFirst = useRef(true);

  const hasValue = (value: { [key: string]: any }) => {
    return defaultKey.some(key => value[key]);
  };

  const getValue = (value: { [key: string]: Array<any> }) => {
    const list: string[] = [];
    defaultKey.forEach(key => {
      const data = value[key];
      const startKey = `start_${key}`;
      const endKey = `end_${key}`;

      if (data && data.length > 0) {
        list.push(
          ...data.map(item => {
            const startValue = item[startKey];
            const endValue = item[endKey] || '';
            return `${key}:${[startValue, endValue].join('~')}`;
          }),
        );
      }
    });

    return list;
  };

  useEffect(() => {
    isFirst.current = false;
    let value = getValue(defaultValue);
    if (value.length === 0) {
      value = options;
    }
    setValue(value);

    if (oriDefaultFilter) {
      let value = getValue(oriDefaultFilter);
      if (value.length === 0) {
        value = options;
      }
      oriDefaultValueRef.current = value;
    }
  }, []);

  useEffect(() => {
    if (!isFirst.current && hasValue(filterValue)) {
      let value = getValue(filterValue);
      if (value.length === 0) {
        value = options;
      }
      setValue(value);
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    if (!DBO_D.compareArray(value, oriDefaultValueRef.current)) {
      return 'dbo-diff-filter';
    }
  }, [value]);

  return [value, setValue, isFirst.current, diffClassName];
}

export function commonDealwithData({
  value,
  defaultKey,
  subDefaultKey,
  dealwithValue = (value: number) => value / 1000,
  unit = 'K',
}: {
  value: any;
  defaultKey: string;
  subDefaultKey?: string;
  dealwithValue?: (value: number) => number;
  unit?: 'K' | '%' | '';
}) {
  const data = value[defaultKey];

  if (data?.length === 0) {
    return { value: '-' };
  }
  const valueKey = subDefaultKey || defaultKey;
  const info = data?.[0] || {};
  let start = info[`start_${valueKey}`] || 0;
  let end = info[`end_${valueKey}`] || 0;

  if (start === 0 && end === 0) {
    return { value: '-' };
  }
  start = dealwithValue(start);
  end = dealwithValue(end);

  if (start !== 0 && end === 0) {
    return { value: `${start}-` };
  }

  return {
    value: [start, end].join('-'),
    optionText: `${start}${unit} ~ ${end}${unit}`,
  };
}

const dealwithThousandDate = (item: {
  value: any;
  defaultKey: string;
  subDefaultKey?: string;
}) => {
  return commonDealwithData(item);
};

export const dealwithPercentageDate = (item: {
  value: any;
  defaultKey: string;
  subDefaultKey?: string;
}) => {
  return commonDealwithData({
    ...item,
    dealwithValue: (value: number) => value * 100,
    unit: '%',
  });
};

export const dealwithCommonDate = (item: {
  value: any;
  defaultKey: string;
  subDefaultKey?: string;
}) => {
  return commonDealwithData({
    ...item,
    dealwithValue: (value: number) => value,
    unit: '',
  });
};

/**
 * 兼容新旧数据格式的处理函数
 * 新格式: { start: number, end?: number }
 * 旧格式: { start_${defaultKey}: number, end_${defaultKey}?: number }
 * 用于 Engagements、Likes、Comments、Shares 等 Range 组件
 */
export const dealwithRangeDataWithNewFormat = ({
  value,
  defaultKey,
}: {
  value: any;
  defaultKey: string;
}) => {
  const data = value[defaultKey];

  if (data?.length === 0) {
    return { value: '-' };
  }
  const info = data?.[0] || {};

  // 优先使用新格式 start/end，回退到旧格式 start_${key}/end_${key}
  let start = info.start ?? info[`start_${defaultKey}`] ?? 0;
  let end = info.end ?? info[`end_${defaultKey}`] ?? 0;

  if (start === 0 && end === 0) {
    return { value: '-' };
  }
  start = start / 1000;
  end = end / 1000;

  if (start !== 0 && end === 0) {
    return { value: `${start}-` };
  }

  return {
    value: [start, end].join('-'),
    optionText: `${start}K ~ ${end}K`,
  };
};

/**
 * 处理单选区间并可以自定义区间的情况
 * 处理值为："1-2", 并添加一个选项1k ~ 2k
[
    {
        "start_comments": 1000,
        "end_comments": 2000
    }
]
 * @param defaultValue
 * @param filterValue
 * @param defaultKey
 * @param options
 * @param dealwithDateFunc
 * @returns
 */
export function useSetRangeDefaultValue({
  defaultValue,
  filterValue,
  oriDefaultFilter = {},
  // backOriDefaultValue,
  defaultKey,
  subDefaultKey,
  options = [],
  dealwithDateFunc = dealwithThousandDate,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  oriDefaultFilter: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
  defaultKey: string;
  subDefaultKey?: string;
  options: Array<{ text: string; value: string }>;
  dealwithDateFunc?: (item: {
    value: any;
    defaultKey: string;
    subDefaultKey?: string;
  }) => { value: string; optionText?: string };
}): {
  rangeValue: string;
  setRangeValue: Dispatch<SetStateAction<string>>;
  followerRef: any;
  isFirst: boolean;
  options: Array<{ text: string; value: string }>;
  diffClassName?: string;
} {
  const [realOptions, setRealOptions] = useState(options);
  const [rangeValue, setRangeValue] = useState('-');
  const followerRef = useRef<any>();
  const oriDefaultValueRef = useRef<any>('-');

  function addOptions(value: string, optionText?: string) {
    setRangeValue(value);
    if (optionText) {
      const index = realOptions.findIndex(item => item.value === value);
      if (index === -1) {
        setRealOptions([...realOptions, { text: optionText, value }]);
      }
    }
  }

  const isFirst = useRef(true);
  useEffect(() => {
    const { value, optionText } = dealwithDateFunc({
      value: defaultValue,
      defaultKey,
      subDefaultKey,
    });
    const { value: oriValue } = dealwithDateFunc({
      value: oriDefaultFilter,
      defaultKey,
      subDefaultKey,
    });

    addOptions(value, optionText);
    oriDefaultValueRef.current = oriValue;
    isFirst.current = false;
  }, []);

  useEffect(() => {
    // todo 这里的 filterValue?.comments 的判断应该有问题，暂时没有影响功能，现在不敢动
    if (!isFirst.current && filterValue?.[defaultKey]) {
      const { value, optionText } = dealwithDateFunc({
        value: filterValue,
        defaultKey,
        subDefaultKey,
      });
      addOptions(value, optionText);
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    if (rangeValue !== oriDefaultValueRef.current) {
      return 'dbo-diff-filter';
    }
  }, [rangeValue]);

  return {
    rangeValue,
    setRangeValue,
    followerRef,
    isFirst: isFirst.current,
    options: realOptions,
    diffClassName,
  };
}

/**
 * 处理下面这种多选的情况
 * 组装成 ["0-5", "5-10"]
[
  {
      "start_helpful": 0,
      "end_helpful": 5
  },
  {
      "start_helpful": 5,
      "end_helpful": 10
  }
]
 * @param defaultValue  默认值
 * @param filterValue   新值
 * @param defaultKey    获取值的key
 * @param startKey      开始的key： start_helpful
 * @param endKey        结束的key： end_helpful
 * @param options       所有选项
 * @returns
 */
export function useSetMultipleRangeDefaultValue({
  defaultValue,
  filterValue,
  defaultKey,
  oriDefaultFilter,
  // backOriDefaultValue,
  startKey,
  endKey,
  options,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  defaultKey: string;
  startKey: string;
  endKey: string;
  oriDefaultFilter: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
  options: Array<{ text: string; value: string }>;
}): {
  rangeValue: Array<string>;
  setRangeValue: Dispatch<SetStateAction<Array<string>>>;
  isFirst: boolean;
  diffClassName?: string;
} {
  const [rangeValue, setRangeValue] = useState<Array<string>>([]);
  const oriDefaultValueRef = useRef<Array<string>>([]);
  function dealwithDateFunc(realValue: Array<{ [key: string]: string }>) {
    if (!realValue || realValue?.length === 0) {
      return options.map(item => item.value);
    }
    const value: any = realValue
      .map((item: any) => {
        const startValue = item[startKey];
        const endValue = item[endKey] || '';
        if (startValue === -99999) return null;
        return [startValue, endValue].join('-');
      })
      .filter(item => item !== null);
    return value;
  }

  const isFirst = useRef(true);
  useEffect(() => {
    const realValue = defaultValue[defaultKey];
    const value = dealwithDateFunc(realValue);
    setRangeValue(value);

    oriDefaultValueRef.current = dealwithDateFunc(
      oriDefaultFilter?.[defaultKey],
    );
    isFirst.current = false;
  }, []);

  useEffect(() => {
    const realValue = filterValue[defaultKey];
    if (!isFirst.current && realValue) {
      const value = dealwithDateFunc(realValue);
      setRangeValue(value);
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    if (!DBO_D.compareArray(rangeValue, oriDefaultValueRef.current)) {
      return 'dbo-diff-filter';
    }
  }, [rangeValue]);

  return {
    rangeValue,
    setRangeValue,
    isFirst: isFirst.current,
    diffClassName,
  };
}

interface ICompareRangeDefaultValueProps {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  defaultKey: string;
  valueKey: string;
  oriDefaultFilter: { [key: string]: any };
  backOriDefaultValue?: (value: any) => void;
  dealwithValueFunc?: (value: string) => string;
}

interface ICompareRangeDefaultValueResult {
  rangeValue: { start: string; end: string; sign: string };
  setRangeValue: Dispatch<
    SetStateAction<{ start: string; end: string; sign: string }>
  >;
  compareRef: any;
  isFirst: boolean;
  diffClassName?: string;
}
/**
 * 处理自定义开闭区间的情况，并没有选项的情况
 * @param defaultValue
 * @param filterValue
 * @param defaultKey
 * @param dealwithDateFunc
 * @returns
 */
export function useCompareRangeDefaultValue({
  defaultValue,
  filterValue,
  defaultKey,
  valueKey,
  oriDefaultFilter = {},
  backOriDefaultValue,
  dealwithValueFunc = (value: string) => value,
}: ICompareRangeDefaultValueProps): ICompareRangeDefaultValueResult {
  const [rangeValue, setRangeValue] = useState({
    start: '0',
    end: '0',
    sign: '>=',
  });
  const oriDefaultValueRef = useRef<any>({
    start: '0',
    end: '0',
    sign: '>=',
  });
  const compareRef = useRef<any>();

  const isFirst = useRef(true);

  const getValue = (value: { [key: string]: Array<any> }) => {
    const data = value[defaultKey];
    const startKey = `start_${valueKey}`;
    const endKey = `end_${valueKey}`;

    if (data && data.length > 0) {
      const value = data[0];
      const sign = value.sign || FILTER_CONST.THAN_RANGE_CUSTOMIZE;
      return {
        start: dealwithValueFunc(value[startKey]),
        end: dealwithValueFunc(value[endKey]),
        sign: sign === FILTER_CONST.THAN_RANGE_CUSTOMIZE ? '' : value.sign,
      };
    }

    return null;
  };

  useEffect(() => {
    isFirst.current = false;
    const data = getValue(defaultValue);
    if (data !== null) {
      setRangeValue(data);
    }
  }, []);

  useEffect(() => {
    if (!isFirst.current && filterValue?.[defaultKey]) {
      const data = getValue(filterValue);
      if (data !== null) {
        setRangeValue(data);
      }

      const oriData = getValue(oriDefaultFilter);
      if (data !== null) {
        oriDefaultValueRef.current = oriData;
      }
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    const { start, end, sign } = rangeValue;

    if (
      !DBO_D.compareDeep(
        { start: dealwithValueFunc(start), end: dealwithValueFunc(end), sign },
        oriDefaultValueRef.current,
      )
    ) {
      return 'dbo-diff-filter';
    }
  }, [rangeValue]);

  return {
    rangeValue,
    setRangeValue,
    compareRef,
    isFirst: isFirst.current,
    diffClassName,
  };
}

export function useSetPeriodDefaultValue({
  defaultValue,
  filterValue,
  initValue,
  initType,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  initValue: [string, string];
  initType: PeriodDateType;
}): {
  value: [string, string];
  setValue: Dispatch<SetStateAction<[string, string]>>;
  isFirst: boolean;
  releaseType: PeriodDateType;
  setReleaseType: Dispatch<SetStateAction<PeriodDateType>>;
} {
  const [value, setValue] = useState<[string, string]>(initValue);
  const [releaseType, setReleaseType] = useState<PeriodDateType>(initType);

  const isFirst = useRef(true);

  function changePeriodValue(newValue: {
    startPeriod: string;
    endPeriod: string;
    releaseType: PeriodDateType;
  }) {
    const { startPeriod, endPeriod, releaseType } = newValue;
    setValue([startPeriod, endPeriod]);
    setReleaseType(releaseType);
  }

  useEffect(() => {
    isFirst.current = false;
    if (filterValue?.endPeriod) {
      changePeriodValue(defaultValue as any);
    }
  }, []);

  useEffect(() => {
    if (!isFirst.current && filterValue?.endPeriod) {
      changePeriodValue(filterValue as any);
    }
  }, [filterValue]);

  return {
    value,
    setValue,
    isFirst: isFirst.current,
    releaseType,
    setReleaseType,
  };
}

/**
 * 设置时间默认值的hook
 * @param defaultValue 默认的对象
 * @param defaultKey   默认的key
 * @param selects      选中项
 * @param setSelects   设置选中项
 * @param options      全选项
 */
export function useGameAndTimeDefaultValue({
  defaultValue,
  filterValue,
  gameInfo,
  allGameList,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  gameInfo: any;
  allGameList: Array<any>;
}): {
  gameInfoAndTime: Array<{ gameId: string; times: string[] }>;
  setGameInfoAndTime: (
    newState: Array<{ gameId: string; times: string[] }>,
  ) => void;
  dateTypeRef: MutableRefObject<ServerTimeType | undefined>;
  dateType: ServerTimeType | undefined;
  setDateType: (value: ServerTimeType) => void;
  value: Array<{ gameId: string; times: string[] }>;
  syncValue: (value: Array<{ gameId: string; times: string[] }>) => void;
} {
  const {
    state: gameInfoAndTime,
    previousState: value,
    setState: setGameInfoAndTime,
    syncStates,
    initStates,
  } = useSyncState<Array<{ gameId: string; times: string[] }>>([]);

  const [dateTypeRef, dateType, setDateType] =
    useStateRef<ServerTimeType>('daily');
  const isFirst = useRef(true);

  function addSpaceList(list: Array<any>) {
    if (list.length < 4) {
      return list.concat(
        Array.from({ length: 4 - list.length }, () => ({
          gameId: '',
          times: [],
        })),
      );
    }

    return list.slice(0, 4);
  }
  const dealwithDate = (data: any) => {
    const { edition_unified_id } = gameInfo;
    const { compare_games } = data;
    let list = [];
    let dateType: any = 'daily';

    if (
      !Array.isArray(compare_games) ||
      !compare_games ||
      compare_games.length === 0
    ) {
      const times = DBO_TM.today('daily');
      list = [
        {
          gameId: edition_unified_id,
          times: [times.start_time, times.end_time],
        },
      ];
    } else {
      const gameList = (compare_games || []).filter((game: any) => {
        return allGameList.includes(game.edition_unified_id);
      });

      list = gameList.map((game: any) => {
        const {
          start_time,
          end_time,
          date_type,
          edition_unified_id: gameId,
        } = game;
        if (gameId === edition_unified_id) {
          dateType = date_type;
        }
        return {
          gameId,
          times: [start_time, end_time],
        };
      });

      list.sort((a: any, b: any) => {
        return a.gameId === edition_unified_id ? -1 : 1;
      });
    }

    setDateType(dateType);
    initStates(addSpaceList(list));
  };

  useEffect(() => {
    dealwithDate(defaultValue);
    isFirst.current = false;
  }, []);

  useEffect(() => {
    if (!isFirst.current && filterValue?.start_time) {
      dealwithDate(filterValue);
    }
  }, [filterValue]);

  return {
    dateTypeRef,
    dateType,
    setDateType,
    gameInfoAndTime,
    setGameInfoAndTime,
    value,
    syncValue: syncStates,
  };
}

export function useCustomInputValue() {
  const {
    state: keywordCustomInput,
    setState: setKeywordCustomInput,
    syncStates: syncCustomInput,
    revertToPrevious: revertCustomInput,
    initStates: initCustomInput,
    previousState: preKeywordCustomInput,
  } = useSyncState('');

  const [customKeywordList, setCustomKeywordList] = useState<string[][]>([]);

  const [keywordSearchType, setKeywordSearchType] =
    useState<KeywordSearchModelType>('exact');

  function reset() {
    initCustomInput('');
    setKeywordSearchType('exact');
  }

  const { renderList, grammarText, grammarTrue } = useMemo(() => {
    if (!keywordCustomInput.trim())
      return {
        renderList: [],
        grammarText: '',
        grammarTrue: true,
      };

    if (keywordSearchType === 'extend' && keywordCustomInput.includes('+')) {
      return {
        renderList: [],
        grammarText:
          'To avoid impacting system performance, Extended Search does not support + combination queries.',
        grammarTrue: false,
      };
    }

    const flag = isValidExpression(keywordCustomInput);
    if (!keywordCustomInput || !flag) {
      return {
        renderList: [],
        grammarText: 'Grammar error',
        grammarTrue: false,
      };
    }

    const list = parseRule(keywordCustomInput);

    if (keywordSearchType === 'extend' && list.length > 20) {
      return {
        renderList: [],
        grammarText:
          'To avoid impacting system performance, Extended Search only supports batch queries of up to 20 keywords.',
        grammarTrue: false,
      };
    }

    if (keywordSearchType === 'exact' && list.length > 200) {
      return {
        renderList: [],
        grammarText:
          'To avoid impacting system performance, Exact Search only supports batch queries of up to 200 keywords.',
        grammarTrue: false,
      };
    }

    return {
      renderList: list,
      grammarText: 'Grammar is correct',
      grammarTrue: true,
    };
  }, [keywordCustomInput, keywordSearchType]);

  return {
    keywordCustomInput,
    setKeywordCustomInput,
    syncCustomInput,
    revertCustomInput,
    initCustomInput,
    preKeywordCustomInput,
    keywordSearchType,
    setKeywordSearchType,
    keywordCustomInputReset: reset,
    customKeywordList,
    setCustomKeywordList,
    renderList,
    grammarText,
    grammarTrue,
  };
}

interface IKeywordDefaultValueProps {
  values: Array<string>;
  setValues: Dispatch<SetStateAction<Array<string>>>;
  vagueValues: Array<string>;
  setVagueValues: Dispatch<SetStateAction<Array<string>>>;
  excludeValues: Array<string>;
  setExcludeValues: Dispatch<SetStateAction<Array<string>>>;
  isFirst: Boolean;
  diffClassName: string | undefined;
  keywordActiveId: 'custom' | 'system';
  preKeywordActiveId: 'custom' | 'system';
  setKeywordActiveId: Dispatch<SetStateAction<'custom' | 'system'>>;
  keywordCustomInput: string;
  setKeywordCustomInput: Dispatch<SetStateAction<string>>;
  customInputClose: () => void;
  customInputConfirm: (value?: string[][]) => void;
  customKeywordList: string[][];
  initKeywordSelect: () => void;
  keywordSearchType: KeywordSearchModelType;
  setKeywordSearchType: Dispatch<SetStateAction<KeywordSearchModelType>>;
  renderList: string[][];
  grammarTrue: boolean;
  grammarText: string;
  keywordListMode: KeywordListModeType;
  setKeywordListMode: Dispatch<SetStateAction<KeywordListModeType>>;
}
/**
 * 设置Keyword筛选器默认值的hook
 * @param defaultValue   默认的对象
 * @param filterValue   实时同步的对象
 * @param defaultKey   默认的key
 * @param selects      选中项
 * @param setSelects   设置选中项
 * @param options      全选项
 * @param watchOptions 监听全选项，因为有些数据是通过接口获取，导致数据没有那么快
 */
export function useKeywordDefaultValue({
  defaultValue,
  filterValue,
  dealwithCustomInput,
  hideSystemTab = false,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  dealwithCustomInput: (value: string) => string[][];
  hideSystemTab?: boolean;
}): IKeywordDefaultValueProps {
  const {
    state: keywordActiveId,
    setState: setKeywordActiveId,
    syncStates: syncActiveId,
    revertToPrevious: revertActiveId,
    initStates: initActiveId,
    previousState: preKeywordActiveId,
  } = useSyncState<'custom' | 'system'>('custom');

  const {
    keywordCustomInput,
    setKeywordCustomInput,
    syncCustomInput,
    revertCustomInput,
    initCustomInput,
    customKeywordList,
    setCustomKeywordList,
    keywordSearchType,
    setKeywordSearchType,
    renderList,
    grammarText,
    grammarTrue,
  } = useCustomInputValue();

  const [values, setValues] = useState<string[]>([]);
  const [vagueValues, setVagueValues] = useState<string[]>([]);
  const [excludeValues, setExcludeValues] = useState<string[]>([]);
  const {
    state: keywordListMode,
    setState: setKeywordListMode,
    previousState: preKeywordListMode,
    syncStates: syncKeywordListMode,
    initStates: initKeywordListMode,
  } = useSyncState<KeywordListModeType>('include');
  const isFirst = useRef(true);

  const defaultValueSet = (data: any) => {
    const {
      keyWords,
      like_math_keywords,
      like_math_exclude_keywords,
      keywordSearchType,
      keywordActiveId = 'custom',
      keywordCustomInput = '',
      keywords_list_mode = 'include',
    } = data || {};

    // 隐藏 system tab 时：回显若为 system，强制扭转到 custom + 空输入状态
    const realActiveId =
      hideSystemTab && keywordActiveId === 'system' ? 'custom' : keywordActiveId;
    const realCustomInput =
      hideSystemTab && keywordActiveId === 'system' ? '' : keywordCustomInput;

    initActiveId(realActiveId);
    initKeywordListMode(keywords_list_mode);
    if (realActiveId === 'system') {
      setValues(Array.isArray(keyWords) ? keyWords : []);
      setVagueValues(
        Array.isArray(like_math_keywords) ? like_math_keywords : [],
      );
      setExcludeValues(
        Array.isArray(like_math_exclude_keywords)
          ? like_math_exclude_keywords
          : [],
      );
    } else {
      const defaultKeywordSearchType = 'exact';
      // keywordSearchType || (has(data, 'merge_keywords_list') ? 'extend' : 'exact');
      setKeywordSearchType(defaultKeywordSearchType);
      initCustomInput(realCustomInput);
      setCustomKeywordList(dealwithCustomInput(realCustomInput));
      // if (isFirst.current) {
      // }
    }

    isFirst.current = false;
  };

  useEffect(() => {
    defaultValueSet(defaultValue);
  }, []);

  useUpdateEffect(() => {
    const { keywordActiveId, keyWords } = filterValue || {};
    const value = keywordActiveId === 'system' ? keyWords : true;

    if (!isFirst.current && value) {
      defaultValueSet(filterValue);
    }
  }, [filterValue]);

  const diffClassName = useMemo(() => {
    // 模式变化时显示 diff 样式
    if (keywordListMode !== "include") {
      return 'dbo-diff-filter';
    }

    if (keywordCustomInput === '' && keywordActiveId === 'custom') {
      return '';
    }

    return 'dbo-diff-filter';
  }, [keywordActiveId, keywordCustomInput, keywordListMode, preKeywordListMode]);

  function customInputClose() {
    revertCustomInput();
    revertActiveId();
    // keywordListMode 不回退，保持用户选择的值
  }

  function customInputConfirm(list?: string[][]) {
    syncActiveId();
    syncKeywordListMode();
    if (keywordActiveId !== 'system') {
      syncCustomInput();
      setCustomKeywordList(list || []);
    }
  }

  function initKeywordSelect() {
    initActiveId('custom');
    setExcludeValues([]);
    setVagueValues([]);
    setValues([]);
    initKeywordListMode('include');
  }

  return {
    values,
    setValues,
    vagueValues,
    setVagueValues,
    excludeValues,
    setExcludeValues,
    preKeywordActiveId,
    keywordActiveId,
    setKeywordActiveId,
    keywordCustomInput,
    setKeywordCustomInput,
    isFirst: isFirst.current,
    diffClassName,
    customInputClose,
    customInputConfirm,
    customKeywordList,
    initKeywordSelect,
    keywordSearchType,
    setKeywordSearchType,
    renderList,
    grammarText,
    grammarTrue,
    keywordListMode,
    setKeywordListMode,
  };
}

export function useTopicInputDefaultValue({
  defaultValue,
  filterValue,
  tabType,
}: {
  defaultValue: { [key: string]: any };
  filterValue: { [key: string]: any };
  tabType?: string;
}) {
  const [topicCustomInput, setTopicCustomInput] = useState<string>('');
  const isFirst = useRef(true);

  const defaultValueSet = (data: any) => {
    const { query = '' } = data || {};

    setTopicCustomInput(query);
    isFirst.current = false;
  };

  useEffect(() => {
    defaultValueSet(defaultValue);
  }, []);

  useUpdateEffect(() => {
    if (!isFirst.current) {
      defaultValueSet(filterValue);
    }
  }, [filterValue]);

  const disabled = useMemo(() => {
    if (tabType === 'topic') {
      return !topicCustomInput?.trim();
    }

    return false;
  }, [topicCustomInput, tabType]);

  return {
    value: topicCustomInput,
    setValue: setTopicCustomInput,
    disabled,
  };
}
